/**
 * The small line icons used in the header and the sidebar. Drawn on a 24-unit
 * grid with a 2-unit stroke so they sit at the same weight as the text beside
 * them, and coloured by currentColor so an active link tints its own icon.
 */
const PATHS = {
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h5v-6h4v6h5V9.5" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  clipboard: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1" />
      <path d="M9 10h6M9 14h6M9 18h3" />
    </>
  ),
  chat: <path d="M4 5h16v11H9l-5 4z" />,
  heart: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />,
  building: (
    <>
      <path d="M4 21V5l8-2v18" />
      <path d="M12 9h8v12" />
      <path d="M2 21h20M7.5 8h1M7.5 12h1M7.5 16h1M15.5 13h1M15.5 17h1" />
    </>
  ),
  plus: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 4.7a3.5 3.5 0 0 1 0 6.6M18 14.3a6.5 6.5 0 0 1 3.5 5.7" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4M9 12h6M9 16h6" />
    </>
  ),
  megaphone: (
    <>
      <path d="M3 10v4h4l8 5V5L7 10z" />
      <path d="M18.5 9a4 4 0 0 1 0 6" />
    </>
  ),
  wallet: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M3 10h18M16 15h2" />
      <path d="M6 6l9-3 1.5 3" />
    </>
  ),
  card: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M2.5 10h19M6.5 15h4" />
    </>
  ),
  bolt: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  list: (
    <>
      <path d="M9 6h12M9 12h12M9 18h12" />
      <path d="M4 6l.01 0M4 12l.01 0M4 18l.01 0" strokeWidth="3" />
    </>
  ),
  gauge: (
    <>
      <path d="M3.5 17a9 9 0 1 1 17 0" />
      <path d="M12 13l4-4" />
      <circle cx="12" cy="13" r="1.2" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M4.6 4.6l2.1 2.1M17.3 17.3l2.1 2.1M2.5 12h3M18.5 12h3M4.6 19.4l2.1-2.1M17.3 6.7l2.1-2.1" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  team: (
    <>
      <circle cx="12" cy="7.5" r="3.5" />
      <circle cx="5" cy="10" r="2.5" />
      <circle cx="19" cy="10" r="2.5" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0M1.5 19a4 4 0 0 1 4.5-4M22.5 19a4 4 0 0 0-4.5-4" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="4.5" />
      <path d="M11.2 11.8 20 3M16 7l3 3M14 9l2 2" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </>
  ),
  chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  shield: <path d="M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6z" />,
  play: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M10 8.5v7l5.5-3.5z" fill="currentColor" />
    </>
  )
};

export const Icon = ({ name, size = 20, className = '' }) => (
  <svg
    className={`icon ${className}`}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {PATHS[name] || PATHS.grid}
  </svg>
);

/**
 * The logo: a house drawn as one line, blue walls under an orange roof, with an
 * H for "homes" standing in the doorway. The name beside it comes from the
 * platform settings, so renaming the platform renames the logo too.
 */
export const BrandLogo = ({ name }) => {
  const words = (name || 'Cameroon Homes').toUpperCase();
  return (
    <span className="brand-logo">
      <svg width="44" height="40" viewBox="0 0 44 40" aria-hidden="true" focusable="false">
        <path d="M4 18 22 4l18 14" fill="none" stroke="#f47b20" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M30 9.5V5h5v8.4" fill="none" stroke="#f47b20" strokeWidth="3.2" strokeLinejoin="round" />
        <path d="M9 18v18h26V18" fill="none" stroke="#1f6fd1" strokeWidth="3.6" strokeLinejoin="round" />
        <path d="M16 23v11M28 23v11M16 28.5h12" stroke="#f47b20" strokeWidth="3.6" strokeLinecap="round" />
      </svg>
      <span className="brand-words">
        <span className="brand-name">{words}</span>
        <span className="brand-tag">MARKETPLACE</span>
      </span>
    </span>
  );
};
